import { React, useRef, useState, useEffect, useMemo, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Tabs, Tab, Box, Grid, IconButton, Typography, Button, Tooltip, Divider, Stack, Skeleton } from '@mui/material';
import Iconify from '../../../../reusable-components/iconify';
import { useDispatch, useSelector } from '../../../../redux/store';
import {
  addInvoiceLog,
  addViewedInvoiceId,
  getInvoiceDataFromRedux,
  getInvoiceBreakdown,
  getInvoiceAnalyzeLogsData,
  clearInvoiceAnalyzeLogs,
  getInvoicesInParsing,
  stopInvoiceParse,
  fetchRelatedInvoices,
} from '../../../../redux/slices/invoicing';
import ReviewInvoice from './ReviewInvoice';
import InvoiceForm from './InvoiceForm';
import MultiFacilityToggle from './MultiFacilityToggle';
import { InvoiceLog } from './InvoiceLog';
import InvoiceChat from './InvoiceChat';
import GlItemsForm from './GlItemsForm';
import GlItemsDataGrid from './GlItemsDataGrid';
import Submit from './SubmitOptions/Submit';
import Approvers from './Approvers';
import { fDateTime } from '../../../../utils/formatTime';
import Scrollbar from '../../../../reusable-components/scrollbar';
import LoadingOverlay from '../../../../reusable-components/loader/LoadingOverlay';
import { InvoiceItemParsedField } from '../../../../utils/globalEnums';
import InvoiceStatusModalButton from '../InvoiceStatusModalButton';
import { InvoiceGLBuilder } from './reusable/InvoiceGLBuilder';
import LoadingAnimationBase from '../../../../reusable-components/loader/LoadingAnimationBase';
import { isActiveParsingStatus } from '../InvoicingParsingStatus/ParsingStatusUtils';
import { useBobAIHighlightRegion } from './reusable/hooks/useBobAIHighlightRegion';
import { RelatedInvoices } from './RelatedInvoices';
import { useIsInvoiceEditable } from './reusable/hooks/useInvoiceLocks';
import InvoiceMenuActions from './InvoiceMenuActions';

InvoiceView.propTypes = {
  invoiceColumnsOrder: PropTypes.array,
};
export default function InvoiceView({ invoiceColumnsOrder }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { isEditable: hasEditAccess } = useIsInvoiceEditable();
  const {
    data: {
      invoiceData,
      invoiceItemData,
      invoiceGLItems,
      invoiceApprovers,
      invoiceLogData,
      invoiceAnalyzeLogs,
      invoiceFiles,
      invoicesInParsingProgress,
      relatedInvoices: { invoiceId: relatedInvoiceId, related = [] },
    },
  } = useSelector((state) => state.invoicing);
  const { subcategories: subcats, manualSubcategories } = invoiceData;

  const { invoice } = invoiceItemData || {};
  const { sourceMethod } = invoice || {};
  const subcategories = sourceMethod === null ? subcats : manualSubcategories || [];
  const manualInvoiceDataLoaded = !(
    invoiceData.manualCategories?.length === 0 ||
    invoiceData.manualSubcategories?.length === 0 ||
    invoiceData.manualFacilities?.length === 0
  );
  const electronicInvoiceDataLoaded = !(
    invoiceData.categories?.length === 0 ||
    invoiceData.subcategories?.length === 0 ||
    invoiceData.facilities?.length === 0 ||
    invoiceData.vendors?.length === 0
  );
  const [openSidebar, setOpenSidebar] = useState(true);
  const [openGlCodeSummary, setOpenGlCodeSummary] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(0);
  const [glRows, setGlRows] = useState([]);
  const [highlightedRows, setHighlightedRows] = useState(null);
  const [highlightMissingGls, setHighlightMissingGls] = useState(false);
  const [highlightedRelatedInvoices, setHighlightedRelatedInvoices] = useState(null);
  const [highlightItem, setHighlightItem] = useState(null);
  const [unsavedData, setUnsavedData] = useState({});
  const [invoiceDataLoaded, setInvoiceDataLoaded] = useState(true);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [editingApprovers, setEditingApprovers] = useState(false);
  const invoiceFormRef = useRef(null);
  const [invoiceFacilityId, setInvoiceFacilityId] = useState(0); // State to track facilityId
  const [unsavedGlData, setUnsavedGlData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [addItemsToChat, setAddItemsToChat] = useState(false);
  const [selectedChatItemIds, setSelectedChatItemIds] = useState([]);
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const selectedChatItems = useMemo(
    () => invoiceItemData?.invoiceItems?.filter((item) => selectedChatItemIds.includes(item.id)) ?? [],
    [invoiceItemData?.invoiceItems, selectedChatItemIds]
  );

  useEffect(() => {
    setSelectedChatItemIds([]);
  }, [invoiceItemData?.invoiceItems]);

  useEffect(() => {
    if (!addItemsToChat) setSelectedChatItemIds([]);
  }, [addItemsToChat]);

  const manualInvoice = invoice?.sourceMethod === 'Manual';

  const ref = useRef(null);
  const leftSideScrollbarRef = useRef(null);
  //fixed bug when user deletes gls when items gls were parsed-don't auto set gls again
  const shouldAutoSetGls = async () => {
    if (invoiceGLItems?.length > 0 || invoice?.sourceMethod !== 'Manual') return true;
    //manual invoices that invoiceItems are categorized but no glItems save-it was intentially deleted.
    if (invoiceItemData?.invoiceItems?.some((item) => (item?.parsedFields & InvoiceItemParsedField.Subcategory) > 0)) {
      return false;
    }
    return true;
  };
  const initializeGlItems = async () => {
    const autoSetGls = await shouldAutoSetGls();
    const glItems = autoSetGls
      ? new InvoiceGLBuilder({ invoiceItemData, subcategories, invoiceGLItems }).getGlItems()
      : [];
    setGlRows(glItems);
  };

  useEffect(() => {
    initializeGlItems();
  }, [invoiceGLItems, invoiceItemData, invoiceData]);

  useEffect(() => {
    if (!invoiceItemData?.invoice) return;
    if (invoiceItemData?.invoice?.id !== Number(id)) return;

    const loadRelatedInvoicesData = async () => {
      await dispatch(fetchRelatedInvoices(invoice.id));
    };
    const loadInvoiceData = async () => {
      if ((manualInvoice && !manualInvoiceDataLoaded) || (!manualInvoice && !electronicInvoiceDataLoaded)) {
        setInvoiceDataLoaded(false);
        await dispatch(getInvoiceDataFromRedux(manualInvoice));
        setInvoiceDataLoaded(true);
      }
    };
    loadInvoiceData();
    loadRelatedInvoicesData();
    if (manualInvoice) {
      dispatch(getInvoiceAnalyzeLogsData(invoice.id));
    } else {
      dispatch(clearInvoiceAnalyzeLogs());
    }
  }, [id, invoiceItemData, invoiceItemData?.invoice?.facilityId]);

  useEffect(() => {
    dispatch(addViewedInvoiceId(id));

    dispatch(
      addInvoiceLog({
        invoiceId: id,
        messageType: 'View',
      })
    );
  }, [id]);

  useEffect(() => {
    if (highlightedRows !== null) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
      const timeout = setTimeout(() => {
        setHighlightedRows(null);
      }, 500);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [highlightedRows]);

  useEffect(() => {
    if (highlightMissingGls) {
      const timeout = setTimeout(() => {
        setHighlightMissingGls(false);
      }, 700);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [highlightMissingGls]);

  useEffect(() => {
    if (highlightedRelatedInvoices !== null) {
      setOpenSidebar(true);
      setTabIndex(1);
      const timeout = setTimeout(() => {
        setHighlightedRelatedInvoices(null);
      }, 500);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [highlightedRelatedInvoices]);

  const relatedInvoicesTabLabel = `Related Invoices (${
    relatedInvoiceId === invoiceItemData?.invoice?.id ? related.length : 0
  })`;

  const tabLabel = (text) => (
    <Tooltip title={text}>
      <Box
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );

  const tabs = (
    <Tabs
      value={tabIndex}
      onChange={handleTabChange}
      sx={{
        minHeight: 'unset',
        minWidth: 0,
        flex: '1 1 auto',
        overflow: 'hidden',
        '& .MuiTabs-flexContainer': {
          minWidth: 0,
        },
        '& .MuiTab-root': {
          color: '#777777',
          fontSize: '14px',
          fontWeight: 'bold',
          textTransform: 'none',
          alignItems: 'center',
          minWidth: 0,
          minHeight: 'unset',
          maxWidth: 220,
          mr: 0.5,
          py: 0.5,
          px: 1,
        },
        '& .MuiTab-root.Mui-selected': {
          color: '#208EE0 !important',
        },
        '& .MuiTabs-indicator': {
          backgroundColor: '#208EE0',
        },
      }}
    >
      <Tab label={tabLabel('Log')} />
      <Tab label={tabLabel(relatedInvoicesTabLabel)} />
    </Tabs>
  );

  useEffect(() => {
    if (highlightItem) {
      if (highlightItem?.form === 'invoiceinfo') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        const leftSideScrollbar = leftSideScrollbarRef.current;
        leftSideScrollbar?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else if (highlightItem?.form !== 'electronicinvoice') {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        });
      }

      const timeout = setTimeout(() => {
        setHighlightItem(null);
      }, 700);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [highlightItem]);

  const { pdfHighlightRegion, handleBobAIHoverStart, handleBobAIHoverEnd } = useBobAIHighlightRegion({
    selectedFileId,
    invoiceFiles,
    invoiceAnalyzeLogs,
  });

  useEffect(() => {
    if (invoiceItemData?.invoice?.multiFacility) setOpenGlCodeSummary(true);
  }, [invoiceItemData?.invoice?.multiFacility]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [highlightMissingGls]);

  useEffect(() => {
    let interval;

    const shouldPoll =
      invoicesInParsingProgress &&
      invoicesInParsingProgress.length > 0 &&
      invoicesInParsingProgress.includes(Number(id));
    if (shouldPoll) {
      interval = setInterval(() => {
        dispatch(getInvoicesInParsing());
      }, 5000);
    } else if (
      isActiveParsingStatus(invoiceItemData?.invoice?.processingStatus, !!invoiceItemData?.invoice?.editInProgress)
    ) {
      dispatch(getInvoiceBreakdown(id, 'ItemsLog'));
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [dispatch, invoicesInParsingProgress, id, invoiceItemData?.invoice?.processingStatus]);

  // grid sizes for info, pdf, and log, with ability to resize info via drag and collapse/expand log
  const totalGrid = 12;
  const defaultInfoWidth = 2.5;
  const [infoWidth, setInfoWidth] = useState(defaultInfoWidth);
  const infoIndex = invoiceColumnsOrder.indexOf('info');
  const logIndex = invoiceColumnsOrder.indexOf('log');
  const remainingWidth = totalGrid - infoWidth - 0.2;
  const pdfWidth =
    invoiceColumnsOrder.includes('pdf') && invoiceColumnsOrder.includes('log') && openSidebar
      ? (remainingWidth * 3) / 4
      : !openSidebar
      ? remainingWidth - 0.4
      : invoiceColumnsOrder.includes('pdf')
      ? remainingWidth
      : 0;
  const logWidth =
    invoiceColumnsOrder.includes('pdf') && invoiceColumnsOrder.includes('log') && openSidebar
      ? remainingWidth / 4
      : invoiceColumnsOrder.includes('log') && openSidebar
      ? remainingWidth
      : openSidebar
      ? 0.4
      : 0;

  const infoGridProps = {
    item: true,
    xs: infoWidth,
    sx: { height: '100%', position: 'relative' },
  };
  const pdfGridProps = {
    item: true,
    xs: pdfWidth,
    sx: { position: 'relative', height: '100%', display: 'flex' },
  };
  const logGridProps = {
    item: true,
    xs: logWidth,
    sx: { height: '100%' },
  };

  const SidebarToggle = ({ direction }) => (
    <Grid
      item
      xs={0.1}
      style={{
        alignSelf: 'stretch',
        position: 'relative',
        height: '100%',
        display: 'flex',
      }}
    >
      <Grid container direction="row" sx={{ zIndex: 2 }}>
        <Tooltip title={openSidebar ? 'Collapse' : 'Expand'}>
          <IconButton
            onClick={() => setOpenSidebar(!openSidebar)}
            sx={{
              position: 'absolute',
              top: 50,
              right: -13,
              backgroundColor: '#f4f6f8',
              zIndex: 2,
              padding: 0,
              '&:hover': {
                color: 'info.main',
                backgroundColor: '#f4f6f8',
              },
            }}
          >
            <Iconify
              icon={`iconamoon:arrow-${
                openSidebar ? direction : direction === 'left' ? 'right' : 'left'
              }-6-circle-bold`}
              width={26}
              height={26}
            />
          </IconButton>
        </Tooltip>
      </Grid>
      <Divider
        orientation="vertical"
        sx={{
          width: '1px',
          minWidth: '1px',
          border: 'none',
          borderRight: '0.3px solid #707070',
          opacity: 0.6,
          backgroundColor: 'transparent',
          zIndex: 1,
        }}
      />
    </Grid>
  );
  const DraggableDivider = ({ direction }) => (
    <Divider
      onMouseDown={(e) => {
        document.body.style.userSelect = 'none';
        const startX = e.clientX;
        const startWidth = infoWidth;
        const handleMouseMove = (moveEvent) => {
          let delta = moveEvent.clientX - startX;
          if (direction === 'left') {
            delta = -delta;
          }
          setInfoWidth(Math.max(2.4, Math.min(3.5, startWidth + delta / 50)));
        };
        const handleMouseUp = () => {
          document.body.style.userSelect = '';
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
      }}
      orientation="vertical"
      sx={{
        height: '100%',
        ml: direction === 'right' ? 2 : 0,
        mr: direction === 'right' ? 0 : 2,
        width: '3px',
        minWidth: '3px',
        border: 'none',
        borderRight: '0.3px solid #707070',
        opacity: 0.6,
        cursor: 'col-resize',
      }}
    />
  );
  const handleStopParsing = async () => {
    const res = await dispatch(stopInvoiceParse(id));
    if (res.status === 200) enqueueSnackbar('Invoice parsing stopped', { variant: 'success' });
    else enqueueSnackbar('Invoice parsing could not be stopped', { variant: 'error' });
  };
  const columnComponents = {
    info: (
      <Grid {...infoGridProps} sx={{ display: 'flex', height: '100%' }} key="info">
        <Scrollbar sx={{ height: '100%' }} scrollableNodeProps={{ ref: leftSideScrollbarRef }}>
          {/* Main: InvoiceForm, GlItemsForm, MultiFacilityToggle */}
          <>
            <Box sx={{ position: 'relative', mb: 3 }}>
              {isActiveParsingStatus(
                invoiceItemData?.invoice?.processingStatus,
                !!invoiceItemData?.invoice?.editInProgress
              ) && (
                <LoadingOverlay
                  spinner={
                    <LoadingAnimationBase
                      vid={
                        'https://res.cloudinary.com/agoraserv/video/upload/v1776795398/Loading%20Gifs/Platform_Solutions_GIF_11_Scroll_001_yvo8oo.webm'
                      }
                      poster={
                        'https://res.cloudinary.com/agoraserv/image/upload/v1776795437/Loading%20Gifs/videoframe_0_4_eiew4r.png'
                      }
                      vidmp4={
                        'https://res.cloudinary.com/agoraserv/video/upload/v1776795398/Loading%20Gifs/Platform_Solutions_GIF_11_Scroll_001_xekkqz.mp4'
                      }
                      wrapperSX={{ display: 'flex', justifyContent: 'center' }}
                    />
                  }
                  text={'Bob AI is reading your invoice'}
                  onStop={hasEditAccess ? handleStopParsing : undefined}
                />
              )}
              <Stack spacing={2}>
                {invoiceItemData?.invoice?.id === Number(id) ? (
                  <InvoiceForm
                    highlightItem={highlightItem}
                    glRows={glRows}
                    setHighlightedGls={setHighlightedRows}
                    setOpenGlCodeSummary={setOpenGlCodeSummary}
                    ref={invoiceFormRef}
                    setInvoiceFacilityId={setInvoiceFacilityId}
                    unsavedGlData={unsavedGlData}
                    setUnsavedGlData={setUnsavedGlData}
                    setSelectedApprovers={setSelectedApprovers}
                    selectedApprovers={selectedApprovers}
                    invoiceDataLoaded={invoiceDataLoaded}
                    handleOpenRelatedInvoices={() => handleTabChange(null, 1)}
                    setHighlightedRelatedInvoices={setHighlightedRelatedInvoices}
                    onBobAIHoverStart={handleBobAIHoverStart}
                    onBobAIHoverEnd={handleBobAIHoverEnd}
                  />
                ) : (
                  <Skeleton variant="rectangular" height={400} />
                )}
                {!invoiceItemData?.invoice?.multiFacility && (
                  <>
                    {invoiceItemData?.invoice?.id === Number(id) ? (
                      <GlItemsForm
                        glRows={glRows}
                        setHighlightMissingGls={setHighlightMissingGls}
                        highlightItem={highlightItem}
                        invoiceFormRef={invoiceFormRef?.current}
                        invoiceFacilityId={invoiceFacilityId}
                        unsavedData={unsavedGlData}
                        setUnsavedData={setUnsavedGlData}
                        invoiceSent={invoice?.sent}
                        invoiceDataLoaded={invoiceDataLoaded}
                      />
                    ) : (
                      <Skeleton variant="rectangular" height={200} />
                    )}
                  </>
                )}
              </Stack>
            </Box>
            {invoiceItemData?.invoice?.id === Number(id) ? (
              <MultiFacilityToggle />
            ) : (
              <Skeleton variant="rectangular" height={30} />
            )}
          </>
        </Scrollbar>
        {infoIndex === 0 && logIndex === 2 && <DraggableDivider direction="right" />}
      </Grid>
    ),
    pdf: (
      <Grid item {...pdfGridProps} sx={{ position: 'relative', height: '100%', display: 'flex' }} gap={2} key="pdf">
        <Grid item gap={2} xs={12}>
          {/* Top: ReviewInvoice */}
          {invoiceItemData?.invoice?.id === Number(id) ? (
            <Box
              sx={{
                borderRadius: '4px',
                boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
                backgroundColor: '#fff',
                border: 'solid 1px #c1c9d0',
                width: '100%',
                mb: '20px',
                padding: '20px',
              }}
            >
              <ReviewInvoice
                selectedFileId={selectedFileId}
                highlightMissingGls={highlightMissingGls}
                setOpenSidebar={setOpenSidebar}
                unsavedData={unsavedData}
                setUnsavedData={setUnsavedData}
                vendor={invoice?.vendor}
                highlightItem={highlightItem}
                invoiceDataLoaded={invoiceDataLoaded}
                onBobAIHoverStart={handleBobAIHoverStart}
                onBobAIHoverEnd={handleBobAIHoverEnd}
                pdfHighlightRegion={pdfHighlightRegion}
                addItemsToChat={addItemsToChat}
                setAddItemsToChat={setAddItemsToChat}
                selectedChatItemIds={selectedChatItemIds}
                setSelectedChatItemIds={setSelectedChatItemIds}
                extraControls={
                  <>
                    {' '}
                    {/* Submit Invoice */}
                    <Stack direction="row" justifyContent="flex-end" alignItems="center">
                      <Box sx={{ display: 'flex' }}>
                        <>
                          {invoice?.sent ? (
                            <Box
                              sx={{
                                color: 'secondary',
                                border: '1px solid #b128bc',
                                borderRadius: '5px',
                                padding: '3px 9px',
                                display: invoice?.invoiceBookedTo ? 'block' : 'none',
                              }}
                            >
                              <Stack spacing={1} direction={'row'} alignItems={'center'}>
                                <Typography color="secondary" sx={{ fontSize: '13px', fontWeight: 'bold' }}>
                                  Submitted on:
                                </Typography>
                                <Typography color="secondary" sx={{ fontSize: '13px' }}>
                                  {fDateTime(invoice?.invoiceBookedTo?.dateAdded)} EST
                                </Typography>
                                <InvoiceStatusModalButton
                                  id={invoice?.id}
                                  total={invoice?.total}
                                  vendor={invoice?.vendor}
                                  bookingVendorStatus={invoice?.bookingVendorStatus}
                                  inline
                                />
                              </Stack>
                            </Box>
                          ) : (
                            <Box>
                              <Submit
                                selectedApprovers={selectedApprovers}
                                editingApprovers={editingApprovers}
                                glRows={glRows}
                                unsavedData={unsavedData}
                                setUnsavedData={setUnsavedData}
                              />
                            </Box>
                          )}
                        </>
                        <InvoiceMenuActions />
                      </Box>
                    </Stack>
                  </>
                }
              />
            </Box>
          ) : (
            <Skeleton variant="rectangular" height={550} />
          )}
          {/* Main: GlItemsDataGrid, etc. */}
          {invoiceItemData?.invoice?.id === Number(id) ? (
            <>
              {invoiceItemData?.invoice?.multiFacility && (
                <GlItemsDataGrid
                  highlightedRows={highlightedRows}
                  highlightItem={highlightItem}
                  rows={glRows}
                  openGlCodeSummary={openGlCodeSummary}
                  setOpenGlCodeSummary={setOpenGlCodeSummary}
                  invoiceDataLoaded={invoiceDataLoaded}
                />
              )}
            </>
          ) : (
            <Skeleton variant="rectangular" height={550} />
          )}
        </Grid>
      </Grid>
    ),
    log: (
      <Fragment key="log">
        {logIndex !== 0 && <SidebarToggle direction={'right'} />}
        {openSidebar ? (
          <Grid item {...logGridProps} sx={{ height: '100%' }}>
            {/* Top: Submit Invoice */}
            {invoiceItemData?.invoice?.id === Number(id) ? (
              <Box
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}
                className="hover-scrollbar"
              >
                <Approvers
                  glRows={glRows}
                  selectedApprovers={selectedApprovers}
                  setSelectedApprovers={setSelectedApprovers}
                  editingApprovers={editingApprovers}
                  setEditingApprovers={setEditingApprovers}
                />
                {tabIndex === 0 && (
                  <InvoiceLog
                    tabs={tabs}
                    setHighlightItem={setHighlightItem}
                    setSelectedFileId={setSelectedFileId}
                    logHeight={invoiceApprovers?.length > 0 ? '350px' : '400px'}
                  />
                )}
                {tabIndex === 1 && (
                  <RelatedInvoices
                    tabs={tabs}
                    logHeight={invoiceApprovers?.length > 0 ? '350px' : '400px'}
                    highlightedRows={highlightedRelatedInvoices}
                  />
                )}
                <InvoiceChat
                  messages={invoiceLogData?.filter((x) => x.messageType === 'Comment')}
                  selectedChatItems={selectedChatItems}
                  addItemsToChat={addItemsToChat}
                  setAddItemsToChat={setAddItemsToChat}
                />
              </Box>
            ) : (
              <>
                <Skeleton variant="rectangular" height={200} />
                <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
                <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
              </>
            )}
          </Grid>
        ) : (
          // Collapsed sidebar with Chat and Invoice Log buttons
          <>
            <Grid item xs={0.4}>
              <Box
                sx={{
                  transform: 'rotate(90deg)',
                  display: 'flex',
                  marginTop: '140px',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ width: '135px', borderRadius: 0 }}
                    onClick={() => setOpenSidebar(true)}
                    startIcon={<Iconify icon={'mdi:approve'} />}
                  >
                    Approvers
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ width: '175px', borderRadius: 0 }}
                    onClick={() => setOpenSidebar(true)}
                    startIcon={<Iconify icon={'pajamas:log'} />}
                  >
                    Invoice log/chat
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </>
        )}
        {logIndex === 0 && <SidebarToggle direction={'left'} />}
      </Fragment>
    ),
  };

  return (
    <div ref={ref} style={{ height: '77.5vh' }}>
      <Grid container spacing={2} alignItems="flex-start" sx={{ height: '100%' }}>
        {invoiceColumnsOrder.map((column) => columnComponents[column])}
      </Grid>
      <div />
    </div>
  );
}
